import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import CreateGodGroupForm from '../components/CreateGodGroupForm';
import EditGodGroupForm from '../components/EditGodGroupForm';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiPlus, FiChevronLeft, FiChevronRight, FiUsers, FiX, FiEye, FiEdit2, FiTrash2, FiSearch, FiCopy } from 'react-icons/fi';

const GodGroupPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalGroups, setTotalGroups] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [members, setMembers] = useState([]);
    const [showViewModal, setShowViewModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedBadge, setCopiedBadge] = useState(false);

    const fetchMembers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setMembers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching members:', err);
            setError(`Failed to fetch members: ${err.response?.data?.error || err.message}`);
        }
    };

    const fetchGroups = async (page, search = '') => {
        try {
            setLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/api/godgroups?page=${page}&limit=20${search ? `&search=${search}` : ''}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                // Transform the data to ensure all member references are populated
                const transformedGroups = response.data.data.map(group => ({
                    ...group,
                    groupMembers: {
                        ...group.groupMembers,
                        handler: group.groupMembers?.handler,
                        participants: group.groupMembers?.participants || []
                    }
                }));
                setGroups(transformedGroups);
                setTotalPages(response.data.totalPages);
                setTotalGroups(response.data.total);
            }
        } catch (err) {
            setError(`Failed to fetch god groups: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
        fetchGroups(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    // Open create form when navigated with ?open=create (e.g. from Layout action bar)
    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateForm(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Add debounce function for search
    const debounce = (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    };

    // Handle search input changes with debounce
    const handleSearchChange = debounce((value) => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page when searching
    }, 300);

    const handleCreateSuccess = (newGroup) => {
        setShowCreateForm(false);
        fetchGroups(currentPage);
    };

    const handleEditClick = (group) => {
        setSelectedGroup(group);
        setShowEditForm(true);
    };

    const handleEditSuccess = (updatedGroup) => {
        setShowEditForm(false);
        setSelectedGroup(null);
        fetchGroups(currentPage);
    };

    const handleDeleteClick = (group) => {
        setGroupToDelete(group);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/godgroups/${groupToDelete._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            setShowDeleteConfirm(false);
            setGroupToDelete(null);
            fetchGroups(currentPage);
        } catch (err) {
            setError(`Failed to delete group: ${err.response?.data?.error || err.message}`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const formatAmount = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(amount);
    };

    const getGroupStatus = (renewalDate) => {
        if (!renewalDate) return 'active';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const renewal = new Date(renewalDate);
        renewal.setHours(0, 0, 0, 0);
        return renewal < today ? 'expired' : 'active';
    };

    const handleView = async (group) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
            const [groupRes, paymentsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/godgroups/${group._id}`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/payments/group/${group._id}`, { headers })
            ]);
            const groupWithUsernames = groupRes.data.success ? groupRes.data.data : group;
            const payments = paymentsRes.data.success ? paymentsRes.data.data : [];
            setSelectedGroup({ ...groupWithUsernames, payments });
            setShowViewModal(true);
        } catch (err) {
            console.error('Error fetching group details:', err);
            setSelectedGroup(group);
            setShowViewModal(true);
        }
    };

    const copyParticipantsToClipboard = () => {
        const participants = selectedGroup?.groupMembers?.participants || [];
        const lines = participants.map((p) => {
            const godName = p.memberID?.godName || p.memberID?.fullName || 'N/A';
            const username = p.username ? (p.username.startsWith('@') ? p.username : `@${p.username}`) : '';
            const part = `${godName} - ${username}`.trim();
            return p.removeDate ? `${part} (Removed ${formatDate(p.removeDate)})` : part;
        });
        const text = lines.join('\n');
        navigator.clipboard.writeText(text).then(() => {
            setCopiedBadge(true);
            setTimeout(() => setCopiedBadge(false), 2000);
        }).catch(() => {
            setCopiedBadge(false);
        });
    };

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">God Groups</h1>
                        <p className="text-gray-600">Total: {totalGroups} groups</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Group
                    </button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="max-w-xl">
                        <div className="relative">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by Group Name..."
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                            Enter group name to search
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {showCreateForm ? (
                    <CreateGodGroupForm
                        onSuccess={handleCreateSuccess}
                        onCancel={() => setShowCreateForm(false)}
                    />
                ) : (
                    <>
                        <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Group Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Handler
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Participants
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Renewal Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-sm">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : groups.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-sm">
                                                No god groups found
                                            </td>
                                        </tr>
                                    ) : (
                                        groups.map((group) => (
                                            <tr key={group._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm">{group.groupName}</td>
                                                <td className="px-6 py-4 text-sm">{group.groupMembers?.handler?.fullName || 'N/A'}</td>
                                                <td className="px-6 py-4 text-sm">
                                                    <div className="space-y-1">
                                                        {group.groupMembers?.participants?.map((participant, index) => (
                                                            <div key={index} className="flex items-center gap-2">
                                                                <span>{participant.memberID?.fullName}</span>
                                                                {participant.removeDate ? (
                                                                    <>
                                                                        <FiX className="text-red-500" title="Removed" />
                                                                        <span className="text-gray-500 text-sm">- {formatDate(participant.removeDate)}</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-gray-500 text-sm">({formatDate(participant.joinDate)})</span>
                                                                )}
                                                            </div>
                                                        )) || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm">{formatDate(group.renewalDate)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                        ${getGroupStatus(group.renewalDate) === 'active' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-red-100 text-red-800'}`}>
                                                        {getGroupStatus(group.renewalDate)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => handleView(group)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditClick(group)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Group"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(group)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Group"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination 
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}

                {showEditForm && selectedGroup && (
                    <EditGodGroupForm
                        group={selectedGroup}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditForm(false);
                            setSelectedGroup(null);
                        }}
                    />
                )}

                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                            <h3 className="text-lg font-medium mb-4">Confirm Delete</h3>
                            <p className="mb-4">Are you sure you want to delete the group "{groupToDelete?.groupName}"?</p>
                            <div className="flex justify-end space-x-2">
                                <button
                                    onClick={() => {
                                        setShowDeleteConfirm(false);
                                        setGroupToDelete(null);
                                    }}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showViewModal && selectedGroup && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                        <div className="relative top-20 mx-auto p-6 border w-[800px] shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-medium">Group Details</h3>
                                <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="space-y-4">
                                {/* Basic Group Information */}
                                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                    <div className="flex items-center">
                                        <span className="font-semibold w-32">Group Name:</span>
                                        <span>{selectedGroup.groupName}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <span className="font-semibold w-32">Handler:</span>
                                        <div>
                                            <span>{selectedGroup.groupMembers?.handler?.fullName || 'N/A'}</span>
                                            {selectedGroup.groupMembers?.handler?.godID && (
                                                <span className="text-gray-500 ml-2 text-sm">
                                                    (ID: {selectedGroup.groupMembers.handler.godID})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <span className="font-semibold w-32">Renewal Date:</span>
                                        <span>{formatDate(selectedGroup.renewalDate)}</span>
                                    </div>
                                </div>

                                {/* Participants Section: one line per participant */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-3">
                                        <h4 className="font-semibold">Participants</h4>
                                        <span className="relative inline-block">
                                            <button
                                                type="button"
                                                onClick={copyParticipantsToClipboard}
                                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                title="Copy participants (godName - username)"
                                                aria-label="Copy participants"
                                            >
                                                <FiCopy className="w-4 h-4" />
                                            </button>
                                            {copiedBadge && (
                                                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                                                    Copied
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="space-y-1">
                                        {selectedGroup.groupMembers?.participants?.length > 0 ? (
                                            selectedGroup.groupMembers.participants.map((participant, index) => {
                                                const fullName = participant.memberID?.fullName || 'N/A';
                                                const username = participant.username ? (participant.username.startsWith('@') ? participant.username : `@${participant.username}`) : '';
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 px-2 rounded text-sm ${participant.removeDate ? 'bg-red-50 text-gray-600' : 'bg-white'}`}
                                                    >
                                                        <span className={participant.removeDate ? 'line-through' : 'font-medium text-gray-900'}>
                                                            {fullName}
                                                        </span>
                                                        {username ? <span className="text-gray-600">{username}</span> : null}
                                                        <span className="text-gray-500">Joined: {formatDate(participant.joinDate)}</span>
                                                        {participant.removeDate && (
                                                            <span className="text-red-600">Removed: {formatDate(participant.removeDate)}</span>
                                                        )}
                                                        {participant.remarks && (
                                                            <span className="text-gray-500 italic">Remarks: {participant.remarks}</span>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-gray-500">No participants</div>
                                        )}
                                    </div>
                                </div>

                                {/* Payment History Section */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold mb-3">Payment History</h4>
                                    <div className="space-y-3">
                                        {selectedGroup.payments?.length > 0 ? (
                                            selectedGroup.payments.map((payment, index) => (
                                                <div key={index} className="bg-white p-3 rounded-md">
                                                    <div className="flex items-center gap-4">
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                            payment.paymentType === 'group' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {payment.paymentType}
                                                        </span>
                                                        <span>Amount: {formatAmount(payment.paymentAmount)}</span>
                                                        <span>Date: {formatDate(payment.paymentDate)}</span>
                                                        {payment.handler && (
                                                            <span>Handler: {payment.handler.fullName}</span>
                                                        )}
                                                    </div>
                                                    {payment.paymentRemarks && (
                                                        <div className="mt-2 text-sm text-gray-600">
                                                            Remarks: {payment.paymentRemarks}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-gray-500">No payment history available</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default GodGroupPage; 